/**
 * Kopf-/Seitenleiste des Tickets (Spec 7.2): Status, Prioritaet, Zuweisung,
 * Frist und Tags. Reine Darstellung — jede Aenderung geht als Callback nach oben.
 *
 * Die anbietbaren Zielzustaende kommen aus `allowedTransitions()` in
 * `@tickethub/shared`; das UI erfindet keine eigene Statuslogik.
 */
import {
  allowedTransitions,
  isOverdue,
  TICKET_PRIORITIES,
  type Tag,
  type Team,
  type TenantMemberDetail,
  type TicketDetail,
  type TicketPriority,
  type TicketStatus,
} from '@tickethub/shared';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

type TicketPropertiesProps = {
  ticket: TicketDetail;
  members: TenantMemberDetail[];
  teams: Team[];
  tags: Tag[];
  readOnly: boolean;
  onStatusChange: (status: TicketStatus) => void;
  onPriorityChange: (priority: TicketPriority) => void;
  onAssigneeChange: (memberId: string | null) => void;
  onTeamChange: (teamId: string | null) => void;
  onDueAtChange: (dueAt: string | null) => void;
  onTagsChange: (tagIds: string[]) => void;
};

/** `<input type="datetime-local">` will `YYYY-MM-DDTHH:mm` in lokaler Zeit. */
function toLocalInput(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function TicketProperties({
  ticket,
  members,
  teams,
  tags,
  readOnly,
  onStatusChange,
  onPriorityChange,
  onAssigneeChange,
  onTeamChange,
  onDueAtChange,
  onTagsChange,
}: TicketPropertiesProps) {
  const { t } = useTranslation();
  const selectedTagIds = new Set(ticket.tags.map((tag) => tag.id));
  const overdue = isOverdue(ticket);

  function toggleTag(tagId: string) {
    const next = new Set(selectedTagIds);
    if (next.has(tagId)) next.delete(tagId);
    else next.add(tagId);
    onTagsChange([...next]);
  }

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="space-y-2">
        <Label htmlFor="ticket-status">{t('ticket.status')}</Label>
        <Select
          id="ticket-status"
          value={ticket.status}
          disabled={readOnly}
          onChange={(event) => onStatusChange(event.target.value as TicketStatus)}
        >
          <option value={ticket.status}>{t(`ticket.status_${ticket.status}`)}</option>
          {allowedTransitions(ticket.status).map((status) => (
            <option key={status} value={status}>
              {t(`ticket.status_${status}`)}
            </option>
          ))}
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ticket-priority">{t('ticket.priority')}</Label>
        <Select
          id="ticket-priority"
          value={ticket.priority}
          disabled={readOnly}
          onChange={(event) => onPriorityChange(event.target.value as TicketPriority)}
        >
          {TICKET_PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {t(`ticket.priority_${priority}`)}
            </option>
          ))}
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ticket-assignee">{t('ticket.assignee')}</Label>
        <Select
          id="ticket-assignee"
          value={ticket.assignee_member_id ?? ''}
          disabled={readOnly}
          onChange={(event) => onAssigneeChange(event.target.value === '' ? null : event.target.value)}
        >
          <option value="">{t('ticket.unassigned')}</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {member.user.name}
            </option>
          ))}
        </Select>
      </div>

      {/* Team-Zuweisung (Spec 8.1): notifiziert alle Teammitglieder; die
          Uebernahme durch eine Person setzt zusaetzlich die Zuweisung oben. */}
      <div className="space-y-2">
        <Label htmlFor="ticket-team">{t('ticket.team')}</Label>
        <Select
          id="ticket-team"
          value={ticket.team_id ?? ''}
          disabled={readOnly}
          onChange={(event) => onTeamChange(event.target.value === '' ? null : event.target.value)}
        >
          <option value="">{t('ticket.no_team')}</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ticket-due">{t('ticket.due_at')}</Label>
        <Input
          id="ticket-due"
          type="datetime-local"
          value={toLocalInput(ticket.due_at)}
          disabled={readOnly}
          onChange={(event) =>
            onDueAtChange(
              event.target.value === '' ? null : new Date(event.target.value).toISOString(),
            )
          }
        />
        {overdue ? (
          <p className="flex items-center gap-1 text-xs text-destructive">
            <AlertTriangle aria-hidden="true" className="size-3" />
            {t('ticket.overdue')}
          </p>
        ) : null}
        {ticket.escalated_at !== null ? (
          <Badge variant="destructive">{t('ticket.escalated')}</Badge>
        ) : null}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">{t('ticket.tags')}</p>
        {tags.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('tag.empty')}</p>
        ) : (
          <ul className="flex flex-wrap gap-1">
            {tags.map((tag) => {
              const active = selectedTagIds.has(tag.id);
              return (
                <li key={tag.id}>
                  <button
                    type="button"
                    disabled={readOnly}
                    aria-pressed={active}
                    onClick={() => toggleTag(tag.id)}
                    className="disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Badge
                      variant={active ? 'default' : 'outline'}
                      style={active ? { backgroundColor: tag.color, borderColor: tag.color } : undefined}
                    >
                      {tag.name}
                    </Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
